/**
 * ResourcesPage — Contribute ▸ Resources
 *
 * Left panel:  My Resources — register, edit, manage personal resources.
 * Right panel: Community resource needs — a ranked match feed (DEV-1147).
 *   Selecting a resource on the left scores/ranks needs against it
 *   (DEV-1148 resourceMatch engine); with nothing selected, needs are
 *   browsed by urgency. A text search narrows the list.
 *
 * Per-need proximity uses real community-hierarchy hop distance, derived
 * here via a tiered community-needs fetch (maxHops 0 → 1 → 2 → full) and
 * passed to the feed as hopByItemGuid.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { ConnectedUserResourceRegistry } from '../../Components/UserResourceRegistry/ConnectedUserResourceRegistry';
import CommunityNeeds from '../../Components/CommunityNeeds/CommunityNeeds';
import SourceModal from '../../Components/SourceModal/SourceModal';
import { offerResource } from '../../api/userResources';
import type { OfferResourcePayload } from '../../api/userResources';
import { fetchCommunityNeeds, submitSource } from '../../api/communityNeeds';
import OfferToNeedModal from '../../Components/OfferToNeedModal/OfferToNeedModal';
import type { CommunityNeedItem } from '../../api/communityNeeds';
import { useUserResources } from '../../hooks/useUserResources';
import { useIsNarrow } from '../../hooks/useIsNarrow';
import {
  loadUserResources,
  setUserResources,
  setUserResourcesError,
} from '../../Data/UserResources/userResourcesStore';
import styles from './ResourcesPage.module.css';

interface Props {
  currentUserGuid: string;
}

/** Default reach is bounded by design (DEV-1147): direct community + one hop.
 *  Broader tiers load only when the user explicitly widens, so the default
 *  payload never walks the whole community hierarchy. The real scale fix —
 *  server-side pagination + matching — is tracked separately under DEV-81. */
const INITIAL_TIERS = [0, 1] as const;
const WIDER_TIERS = [2, 5] as const;
/** Projects in these statuses never surface as open needs. */
const EXCLUDED_PROJECT_STATUSES = new Set(['Completed', 'Abandoned']);

export default function ResourcesPage({ currentUserGuid }: Props) {
  const { t } = useAppTranslation('userProfile');

  // Registered resources + selection now live in the shared store (Rails
  // Phase 2): the registry lives in the App-mounted ResourceRegistryRail on
  // wide screens, and the needs column below reads the same selection so the
  // select→match coupling survives. On narrow screens the Rail yields and we
  // host the registry in-page (single instance, breakpoint-gated).
  const { resources, selectedResource, selectedResourceGuid, error: resourcesError } =
    useUserResources();
  const isNarrow = useIsNarrow(960);

  // Self-sufficient fetch (deduped in the store): the page doesn't depend on
  // the Rail being mounted to have resource data for needs ranking / offers.
  useEffect(() => {
    void loadUserResources(currentUserGuid);
  }, [currentUserGuid]);

  // Right: Community needs + per-need hop distance
  const [communityNeeds, setCommunityNeeds] = useState<CommunityNeedItem[]>([]);
  const [hopByItemGuid, setHopByItemGuid] = useState<Record<string, number>>({});
  const [isLoadingNeeds, setIsLoadingNeeds] = useState(true);

  const [searchText, setSearchText] = useState('');
  const [sourceNeed, setSourceNeed] = useState<CommunityNeedItem | null>(null);
  const [widened, setWidened] = useState(false);
  const [isWidening, setIsWidening] = useState(false);
  const [offerNeed, setOfferNeed] = useState<CommunityNeedItem | null>(null);

  // The backend already scopes to public + seeking/partial needs. We add two
  // client guards: drop needs from completed/abandoned projects, and drop
  // threshold needs that are quantitatively satisfied (current >= target) but
  // whose state wasn't auto-advanced to 'met' — i.e. "0 still needed".
  const activeCommunityNeeds = useMemo(
    () => communityNeeds.filter((n) => {
      const status = (n.project_status || '').trim();
      if (status && EXCLUDED_PROJECT_STATUSES.has(status)) return false;
      if (n.target_quantity != null && n.current_quantity >= n.target_quantity) return false;
      return true;
    }),
    [communityNeeds]
  );

  // Tiered community-needs fetch: assign each need the smallest hop at which
  // it first appears. Renders the closest tier fast, then widens in the bg.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoadingNeeds(true);
      setWidened(false);
      setCommunityNeeds([]);
      setHopByItemGuid({});
      const hopMap: Record<string, number> = {};
      const seen = new Set<string>();
      const merged: CommunityNeedItem[] = [];

      // First appearance defines a need's true hop distance.
      for (let i = 0; i < INITIAL_TIERS.length; i++) {
        const hops = INITIAL_TIERS[i];
        let batch: CommunityNeedItem[];
        try {
          batch = await fetchCommunityNeeds(currentUserGuid, hops);
        } catch (error) {
          console.error('[ResourcesPage] community-needs fetch failed at maxHops', hops, error);
          continue;
        }
        if (cancelled) return;
        for (const n of batch) {
          if (!seen.has(n.item_guid)) {
            seen.add(n.item_guid);
            hopMap[n.item_guid] = hops;
            merged.push(n);
          }
        }
        setCommunityNeeds([...merged]);
        setHopByItemGuid({ ...hopMap });
        if (i === 0) setIsLoadingNeeds(false); // show direct-community needs immediately
      }
      if (!cancelled) setIsLoadingNeeds(false);
    })();
    return () => { cancelled = true; };
  }, [currentUserGuid]);

  // Widen on demand: fetch the broader tiers and append. Bounds the default
  // view to nearby needs while still allowing an explicit wider search.
  const handleWiden = useCallback(async () => {
    setIsWidening(true);
    try {
      const seen = new Set(communityNeeds.map((n) => n.item_guid));
      const hopMap: Record<string, number> = { ...hopByItemGuid };
      const merged = [...communityNeeds];
      for (const hops of WIDER_TIERS) {
        let batch: CommunityNeedItem[];
        try {
          batch = await fetchCommunityNeeds(currentUserGuid, hops);
        } catch (error) {
          console.error('[ResourcesPage] widen fetch failed at maxHops', hops, error);
          continue;
        }
        const assignedHop = hops >= 3 ? 3 : hops; // maxHops 5 → "beyond" (3+)
        for (const n of batch) {
          if (!seen.has(n.item_guid)) {
            seen.add(n.item_guid);
            hopMap[n.item_guid] = assignedHop;
            merged.push(n);
          }
        }
      }
      setCommunityNeeds(merged);
      setHopByItemGuid(hopMap);
      setWidened(true);
    } finally {
      setIsWidening(false);
    }
  }, [currentUserGuid, communityNeeds, hopByItemGuid]);

  const handleSourceSubmit = useCallback(
    async (url: string | null, note: string | null, sourceType: string) => {
      if (!sourceNeed) return;
      const result = await submitSource(currentUserGuid, sourceNeed.item_guid, url, note, sourceType);
      setCommunityNeeds((prev) =>
        prev.map((n) => (n.item_guid === sourceNeed.item_guid ? { ...n, source_count: n.source_count + 1 } : n))
      );
      return result;
    },
    [sourceNeed, currentUserGuid]
  );

  // Offer flow (DEV-1149): the match feed's Offer button opens the modal;
  // confirming calls the existing offer handshake and flips the resource to
  // 'reserved' so the left pane reflects it.
  const handleOffer = useCallback((need: CommunityNeedItem) => {
    setOfferNeed(need);
  }, []);

  const handleConfirmOffer = useCallback(async (resourceGuid: string, payload: OfferResourcePayload) => {
    const updated = await offerResource(resourceGuid, payload);
    setUserResources((prev) => prev.map((r) => (r.resource_guid === updated.resource_guid ? updated : r)));
  }, []);

  return (
    <div className={styles.container}>
      {resourcesError && (
        <div className={styles.errorBanner}>
          <div className={styles.errorMessage}>{resourcesError}</div>
          <button onClick={() => setUserResourcesError(null)} className={styles.closeButton} type="button" aria-label="Close error">✕</button>
        </div>
      )}

      <div className={`${styles.pageLayout} ${isNarrow ? styles.twoCol : styles.oneCol}`}>
        {/* Left: My Resources — hosted in the Rail on wide screens; rendered
            in-page only as the narrow-screen fallback (Rails Phase 2). */}
        {isNarrow && (
          <div className={styles.leftPanel}>
            <ConnectedUserResourceRegistry currentUserGuid={currentUserGuid} />
          </div>
        )}

        {/* Right: Community resource needs (ranked match feed) */}
        <div className={styles.rightPanel}>
          <div className={styles.panelHeader}>
            <div className={styles.panelHeaderLeft}>
              <span className={styles.panelHeaderIcon}>📢</span>
              <h2 className={styles.panelHeadingBar}>{t('resources.communityResourceNeeds')}</h2>
            </div>
          </div>
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={t('resources.searchNeedsPlaceholder')}
            aria-label={t('resources.searchNeedsPlaceholder')}
            style={{
              width: '100%',
              background: 'var(--bg-tertiary)',
              border: '1px solid var(--border-default)',
              borderRadius: 8,
              color: 'var(--text-primary)',
              font: 'inherit',
              fontSize: 13,
              padding: '9px 11px',
              outline: 'none',
              marginBlockEnd: 12,
            }}
          />
          <CommunityNeeds
            needs={activeCommunityNeeds}
            selectedResource={selectedResource}
            hopByItemGuid={hopByItemGuid}
            searchText={searchText}
            onSourceClick={(need) => setSourceNeed(need)}
            onOffer={handleOffer}
            isLoading={isLoadingNeeds}
            onWiden={widened ? undefined : handleWiden}
            isWidening={isWidening}
          />
        </div>
      </div>

      {sourceNeed && (
        <SourceModal
          need={sourceNeed}
          onSubmit={async (url, note, sourceType) => { await handleSourceSubmit(url, note, sourceType); }}
          onClose={() => setSourceNeed(null)}
        />
      )}

      {offerNeed && (
        <OfferToNeedModal
          need={offerNeed}
          resources={resources}
          defaultResourceGuid={selectedResourceGuid}
          onConfirm={handleConfirmOffer}
          onClose={() => setOfferNeed(null)}
        />
      )}
    </div>
  );
}
